import { RequestDeadlineError, throwIfAborted, withRequestDeadline } from './request-deadline';

/**
 * Обращение к API.
 *
 * ОДИН ИСТОЧНИК, ПОЭТОМУ НИКАКИХ КЛЮЧЕЙ В КОДЕ СТРАНИЦЫ. Ключ доступа
 * лежит в печенье с признаком httpOnly — его нельзя прочитать скриптом,
 * и этим обесценивается целый класс атак через внедрённый код. Обратная
 * сторона: страница НЕ ЗНАЕТ, вошёл человек или нет, и узнать может
 * только попыткой. Отсюда устройство ниже.
 */

const BASE = '/api/rk';

/** Отказ, о котором API рассказал по-человечески. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /**
     * Тело отказа целиком.
     *
     * НУЖНО РОВНО ДЛЯ ПОДРОБНОСТЕЙ, КОТОРЫЕ НЕ ВЛЕЗАЮТ В СТРОКУ.
     * Отказ «требуется подтверждение» несёт с собой `danger` — сколько
     * запросов будет исключено и какие именно. Текстом это уже есть в `message`,
     * но списком его показывать нельзя, а человек узнаёт свои рабочие
     * фразы именно списком.
     */
    readonly body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Не вошёл или ключ протух. Обрабатывается отдельно: это не «ошибка», а состояние. */
  get needsLogin(): boolean {
    return this.status === 401;
  }
}

/** Only explicit refresh denials mean signed out; other 400/403 responses do not. */
export function isTerminalRefreshDenial(error: ApiError): boolean {
  return error.status === 401 ||
    (error.status === 400 && ['no_session', 'revoked', 'expired', 'reuse_detected'].includes(error.code)) ||
    (error.status === 403 && error.code === 'blocked');
}

/**
 * Обновление ключа — не чаще одного раза за раз.
 *
 * Кабинет открывает несколько запросов сразу (кампании, кластеры,
 * достоверность). Ключ протухает раз в пятнадцать минут, и тогда все они
 * получают 401 одновременно. Без общего обещания каждый пошёл бы обновлять
 * ключ сам: пять одновременных обновлений, четыре из которых сразу же
 * обесценивают ключ, полученный пятым, — и человека выкидывает на вход
 * без всякой причины.
 */
let refreshing: Promise<boolean> | null = null;

/**
 * ═══════════════════════════════════════════════════════════════════════
 * МЕТКА ПРОТИВ ПОДЛОГА ЗАПРОСОВ.
 *
 * Сервер ставит при входе печенье с меткой — единственное БЕЗ `httpOnly`,
 * нарочно: прочитать его должен ровно этот код и вернуть заголовком.
 * Чужой сайт прочитать печенье не может, поэтому подделать заголовок
 * ему неоткуда.
 *
 * Тайны в метке нет: доступа она не даёт, она доказывает, что запрос
 * сделан НАШЕЙ страницей. Второй заслон к проверке источника на сервере —
 * тот держится на заголовке, который ставит браузер, а этот на нашем
 * собственном коде.
 *
 * Имя печенья с приставкой `__Host-` в бою и без неё на машине
 * разработчика: приставка требует `Secure`, а локально кабинет живёт
 * по http. Ищем оба имени — здесь это безопасно, потому что метка
 * не открывает ничего.
 * ═══════════════════════════════════════════════════════════════════════
 */
function метка(): string | null {
  for (const кусок of document.cookie.split(';')) {
    const [k, ...v] = кусок.trim().split('=');
    if (k === '__Host-csrf' || k === 'csrf') return decodeURIComponent(v.join('='));
  }
  return null;
}

/** Заголовки изменяющего запроса: разбор тела плюс метка. */
function заголовки(сТелом: boolean): Record<string, string> {
  const h: Record<string, string> = сТелом ? { 'content-type': 'application/json' } : {};
  const м = метка();
  if (м !== null) h['x-csrf-token'] = м;
  return h;
}

async function withClientDeadline<T>(
  operation: (signal: AbortSignal) => Promise<T>, signal?: AbortSignal,
): Promise<T> {
  try {
    return await withRequestDeadline(operation, { signal });
  } catch (error) {
    if (error instanceof RequestDeadlineError) {
      throw new ApiError(0, 'request_timeout', 'Сервер долго отвечает. Повторите попытку.');
    }
    throw error;
  }
}

function refreshOnce(): Promise<boolean> {
  refreshing ??= withClientDeadline(async signal => {
    const r = await fetch(`${BASE}/auth/refresh`, {
      method: 'POST', credentials: 'same-origin', headers: заголовки(true), body: '{}', signal,
    });
    if (r.status === 401) return false;
    if (!r.ok) {
      const error = await asError(r);
      if (isTerminalRefreshDenial(error)) return false;
      throw error;
    }
    return true;
  })
    .catch((error: unknown) => {
      if (error instanceof ApiError && !(error.status === 0 && error.code === 'request_timeout')) throw error;
      // An unknown refresh outcome must not trigger an implicit POST replay.
      throw new ApiError(0, 'session_unavailable',
        'Не удалось обновить вход. Проверьте соединение и обновите страницу.');
    })
    .finally(() => { refreshing = null; });
  return refreshing;
}

interface Options {
  method?: string;
  /**
   * Тело запроса ОБЪЕКТОМ, а не строкой.
   *
   * Строка здесь запрещена типом намеренно. `api` кодирует тело сам,
   * и переданная уже закодированной строка кодируется ВТОРОЙ раз:
   * на сервер уходит `"{\"on\":true}"` — строка, а не объект. Разбор
   * такого тела даёт либо отказ разбора, либо пустые поля, и выглядит
   * это как «кнопка не работает», а не как ошибка в вызове.
   * Так 09.08 молча ломались сохранение расписания и обновление заказов.
   */
  body?: Record<string, unknown> | unknown[];
  signal?: AbortSignal;
  keepalive?: boolean;
}

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const authenticationAttempt = ['/auth/refresh', '/auth/login', '/auth/login/totp'].includes(path);
  const method = (options.method ?? 'GET').toUpperCase();
  const bounded = method === 'GET' || method === 'HEAD' || authenticationAttempt ||
    (method === 'POST' && /\/bid-commands$/.test(path)); // Immutable requestId makes only queue admission replayable.
  let finalUnauthorized = false;
  let refreshPending = false;
  const execute = async (signal?: AbortSignal): Promise<T> => {
    const send = (): Promise<Response> => {
      throwIfAborted(signal);
      return fetch(BASE + path, {
        method: options.method ?? 'GET', credentials: 'same-origin',
        headers: заголовки(options.body !== undefined),
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        ...(signal ? { signal } : {}),
        ...(options.keepalive ? {keepalive:true} : {}),
      });
    };
    let res = await send();
    throwIfAborted(signal);
    if (res.status === 401 && !authenticationAttempt) {
      // A caller deadline can expire while this shared POST is still pending.
      refreshPending = true;
      let refreshed: boolean;
      try { refreshed = await refreshOnce(); }
      finally { refreshPending = false; }
      if (refreshed) {
        res = await send();
        throwIfAborted(signal);
      }
    }
    // The initial401 is not a final denial while refresh has an unknown outcome.
    finalUnauthorized = res.status === 401;
    if (!res.ok) throw await asError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  };
  try {
    return await (bounded ? withClientDeadline(execute, options.signal) : execute(options.signal));
  } catch (error) {
    if (finalUnauthorized && error instanceof ApiError && error.code === 'request_timeout') {
      throw new ApiError(401, 'unauthorized', 'Войдите, чтобы продолжить.');
    }
    if (refreshPending && error instanceof ApiError && error.status === 0 && error.code === 'request_timeout') {
      // Do not expose an unknown refresh outcome as a retryable read timeout.
      throw new ApiError(0, 'session_unavailable',
        'Не удалось обновить вход. Проверьте соединение и обновите страницу.');
    }
    throw error;
  }
}

/**
 * Отказ в понятный вид.
 *
 * API отвечает `{ error, code }` — текст уже написан там, где известна
 * причина (см. `src/http/errors.ts`). Придумывать свои формулировки
 * на стороне страницы значило бы держать два набора слов для одного
 * события, и они разойдутся.
 */
async function asError(res: Response): Promise<ApiError> {
  let code = 'error';
  let message = `Сервис ответил ошибкой ${res.status}`;
  let body: Record<string, unknown> = {};

  try {
    body = (await res.json()) as Record<string, unknown>;
    if (typeof body.error === 'string' && body.error) message = body.error;
    if (typeof body.code === 'string' && body.code) code = body.code;

    // ЧУЖОЙ 404 — ЭТО НЕ «НЕ НАЙДЕНО», А «ТАКОГО АДРЕСА НЕТ». Свои отказы
    // всегда несут `code`; отказ без него приходит от маршрутизатора,
    // и означает он ровно одно: кабинет зовёт адрес, которого сервер
    // не знает. Почти всегда — потому что браузер держит сборку новее
    // серверной. Слова «Not Found» в углу экрана об этом не говорят
    // ничего, и разбираться приходится с нуля.
    if (res.status === 404 && code === 'error') {
      code = 'no_route';
      message =
        'Сервис не знает такого адреса. Обычно это значит, что кабинет ' +
        'новее сервера: обновите страницу с очисткой — Ctrl+F5.';
    }
  } catch {
    // Тело не JSON. Самая частая причина — раздача статики перехватила
    // адрес API и вернула index.html; см. exclude в app.module.ts.
    // Оставляем общий текст: подробности всё равно будут в консоли сети.
  }

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * 502, 503, 504 — ЭТО НЕ ОТКАЗ СЕРВИСА, А ЕГО МОЛЧАНИЕ.
   *
   * 05.09.2026 владелец нажал «Остановить» и получил серую рамку
   * «Сервис ответил ошибкой 502». Строка верна и бесполезна: она
   * не говорит ни что случилось, ни что делать.
   *
   * ЧТО ЭТИ ТРИ КОДА ЗНАЧАТ НА САМОМ ДЕЛЕ. Их отдаёт ПРОКСИ, а не наша
   * служба: до службы он не достучался (502), она не принимает (503)
   * или не ответила в срок (504). Своих отказов служба такими кодами
   * не отдаёт вовсе — у неё всегда есть тело с `error` и `code`,
   * и до этого места они не доходят.
   *
   * Почти всегда причина одна и та же и безобидная: служба
   * перезапускается после выкатки, и это занимает до минуты.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * ГЛАВНОЕ ЗДЕСЬ — НЕ «ПОДОЖДИТЕ», А «ПРОВЕРЬТЕ, НЕ СДЕЛАНО ЛИ УЖЕ».
   *
   * Прокси не знает, дошёл ли запрос до службы. Он мог не дойти вовсе —
   * а мог дойти, отработать и потерять ОТВЕТ на обратном пути. Для
   * чтения разницы нет, а для действия она решает: остановка теста,
   * ушедшая в такой отказ, могла и произойти.
   *
   * Поэтому текст просит обновить, а не повторить вслепую, а сами
   * действия перечитывают данные и в ветке отказа — см. `onError`
   * у остановки теста.
   * ═══════════════════════════════════════════════════════════════════════
   */
  /**
   * ═══════════════════════════════════════════════════════════════════
   * 413 — ЭТО ОБРЫВ ТЕЛА ДО СЛУЖБЫ, И ОБЪЯСНИТЬ ЕГО МОЖЕТ ТОЛЬКО ЗДЕСЬ.
   *
   * Потолок тела стоит на express, ПЕРЕД управлением: не влезло —
   * запрос обрывается, и контроллер, который умеет сказать «файл 12 МБ,
   * потолок 10», до дела не доходит. Ответ приходит страницей express,
   * а не нашим `{error, code}`, поэтому разбор выше его и не увидел:
   * `res.json()` бросил, и осталось «Сервис ответил ошибкой 413».
   *
   * Такое бывает ровно на загрузках, и слово «большой» здесь и есть
   * весь ответ.
   * ═══════════════════════════════════════════════════════════════════
   */
  if (code === 'error' && res.status === 413) {
    code = 'too_large';
    message =
      'Файл слишком большой — служба оборвала его, не начав читать. ' +
      'Снимок карточки принимается до 10 МБ.';
  }

  if (code === 'error' && (res.status === 502 || res.status === 503 || res.status === 504)) {
    code = 'gateway';
    message =
      res.status === 504
        ? 'Сервис не ответил вовремя. Действие могло всё-таки пройти — ' +
          'обновите страницу и посмотрите, прежде чем повторять.'
        : 'Сервис сейчас не отвечает — обычно это перезапуск после ' +
          'обновления, он занимает до минуты. Действие могло всё-таки ' +
          'пройти: обновите страницу и посмотрите, прежде чем повторять.';
  }

  return new ApiError(res.status, code, message, body);
}
